import mixpanel from 'mixpanel-browser';
import AppConfig from '../appConfig';
import resolveAnalyticsEnvironment from './analyticsEnvironment';

export const MIXPANEL_EVENTS = {
    CHANGE_DATA: 'changeData',
    CREATE_REPORT: 'createReport',
    SHARE_BUTTON: 'shareButton',
    EDIT_DEAL_FROM_INSIGHTS: 'editDealFromInsights',
    STANDARD_BASEMAP_SELECTED: 'standardBasemapSelected',
    SATELLITE_BASEMAP_SELECTED: 'satelliteBasemapSelected',
    STREET_BASEMAP_SELECTED: 'streetBasemapSelected',
    CHANGE_DATA_SELECTION: 'changeDataSelection',
    SUPPORT_OPEN: 'supportOpen',
    TRIGGER_FEEDBACK_MODAL: 'triggerFeedbackModal',
    TAKE_TOUR: 'takeTour',
    REPORT_MISSING_DATA_OPEN: 'reportMissingDataOpen',
    SUGGEST_EDIT_FACILITY_OPEN: 'suggestEditFacilityOpen',
    SWITCH_ISOLINE_TYPE: 'switchIsolineType',
    TRIGGER_KNOWLEDGE_BASE: 'triggerKnowledgeBase',
    UPSELL_OPPORTUNITY: 'upsellOpportunity',
    VIEW_FACILITY_PIN_CONFIG: 'viewFacilityPinConfig',
    LEARN_MORE_CLICK: 'learnMoreClick',
    CHANGE_MAP_LAYER: 'changeMapLayer',
    VIEW_MAP_LAYERS: 'viewMapLayers',
    RESET_MAP_DEFAULTS: 'resetMapDefaults',
};

export const PIN_CONFIG_VIEW_TRIGGER = {
    LOAD: 'load', // default mode shown on initial map load
    USER_TOGGLE: 'user_toggle', // deliberate user switch
    HIGHLIGHT_SELECT: 'highlight_select', // user changed the by-type highlight metric
};

export const MAP_LAYER_ACTION = {
    ADD: 'add',
    REMOVE: 'remove',
    SHOW: 'show',
    HIDE: 'hide',
};

export const MIXPANEL_LOCATIONS = {
    topNav: 'Top Nav',
    pointInfoConstructions: 'Point Info - Commercial Development',
    pointInfoHousings: 'Point Info - Housing Starts',
    noDealsInList: 'No Deals In List',
};

class Mixpanel {
    static isMixpanelEnabled() {
        return AppConfig.constants.appIds.mixpanelToken !== null;
    }

    static init() {
        // init mixpanel
        if (this.isMixpanelEnabled()) {
            mixpanel.init(AppConfig.constants.appIds.mixpanelToken, {
                debug: AppConfig.constants.mixpanelDebug,
                ignore_dnt: true,
                track_pageview: false,
                autocapture: false,
                persistence: 'localStorage',
            });
            mixpanel.register({ ...resolveAnalyticsEnvironment(), appSurface: 'map' });
        }
    }

    /**
     *
     * @param {number} userId
     * @param {string} userName
     * @param {string} userProduct
     * @param {string} userEmail
     * @param {number} activeOrganizationId
     * @param {Array} organizations
     * @returns {undefined} no output
     */
    static identifyAndSetUser(
        userId,
        userName,
        userProduct,
        userEmail,
        activeOrganizationId,
        organizations = [],
    ) {
        if (!this.isMixpanelEnabled()) return;

        mixpanel.identify(userId);
        mixpanel.people.set({
            $name: userName,
            $email: userEmail,
            $product: userProduct,
            $organizations: organizations.map(org => org.name),
        });

        if (activeOrganizationId) {
            mixpanel.set_group('organization_id', activeOrganizationId);
        }

        mixpanel.track_pageview();
    }
    /**
     *
     * @param {string} eventName
     * @param {Object} eventProperties
     * @returns {undefined} no output
     */
    static emitUserEvent(mixpanelEvent, eventProperties = {}) {
        // Telemetry must never break the UI — swallow analytics errors so a
        // failing track() can't abort a caller's handler before its real action.
        try {
            if (this.isMixpanelEnabled()) {
                mixpanel.track(mixpanelEvent, eventProperties);
            }
        } catch (error) {
            console.warn('[Mixpanel] emitUserEvent failed', error);
        }
    }
}

export default Mixpanel;
